import { z } from "zod";

import { VibeAppDeployabilityReadSchema } from "../../../../shared/domain/vibe/app-deployability";
import { VibeAppDeploymentHistorySchema } from "./app-deployment-history.schemas";
import { VibeAppDeploymentSummarySchema } from "./app-deployment-summary.schemas";
import { VibeAppCreatorSchema, VibeAppGitProjectSummarySchema } from "./app-summaries.schemas";
import { VibeAppSchema } from "./core";

// List apps
//
// Wrapped in an object so additive top-level fields (cursor, total)
// land later without breaking every consumer of `useList`.

/**
 * List item = the app plus TWO independent deployment signals. They answer
 * different questions and routinely disagree, so the console must never
 * collapse them into one badge:
 *
 * - `latestDeployment` — the newest attempt, ANY status. Answers "did the
 *   newest build succeed?". `null` for an app that has never been deployed.
 * - `servingDeployment` — the deployment occupying the live slot (the newest
 *   `HEALTHY` row). Answers "is my app up?". `null` when nothing is serving —
 *   either never deployed, or every version is down.
 *
 * A failed newest build over a still-serving older version is the normal case
 * after a bad push: `latestDeployment.status === "FAILED"` while
 * `servingDeployment` is a healthy earlier version, and the app answers 200.
 * The live slot is exclusive (cutover supersedes the predecessor), so
 * `servingDeployment` is a single row, never a set.
 */
export const VibeAppListItemSchema = VibeAppSchema.extend({
  latestDeployment: VibeAppDeploymentSummarySchema.nullable(),
  servingDeployment: VibeAppDeploymentSummarySchema.nullable(),
  /**
   * Whether this app can deploy right now — see `GetVibeAppResponseSchema`.
   * Carried on the list so the apps grid can mark an app that will never
   * build without an N+1 of git-project fetches. Before this field the grid
   * could only say "Never deployed", which reads identically for an app
   * nobody has pushed to and an app that has no source at all.
   */
  deployability: VibeAppDeployabilityReadSchema,
  /**
   * The git project attached to this app — see `GetVibeAppResponseSchema`.
   * Carried on the list for the same reason `deployability` is: the grid
   * renders the Code-tab affordance per app without an N+1 of git-project
   * fetches.
   */
  gitProject: VibeAppGitProjectSummarySchema.nullable(),
  /**
   * Who created this app, resolved from the `VibeAppCreatedBy` relation, or
   * `null`. The app carries `createdByUserId` already — a raw id renders as
   * nothing a human can read, which is the whole reason this exists beside it.
   *
   * Sits on the LIST ITEM and not on `VibeAppSchema` for the same reason
   * `deployability` and `gitProject` do, stated in full on
   * `GetVibeAppResponseSchema`: it is a JOIN, not app state. On the app schema
   * it would oblige every producer of a `VibeApp` — create included, which has
   * the caller's id and no user row in hand — to resolve one.
   *
   * `null` covers two states the console renders identically and must not
   * crash on: the column is NULL (the app was created by an API key, or by a
   * platform path with no user), and the relation resolves to nothing because
   * the user was deleted — `onDelete: SetNull` on `VibeAppCreatedBy` turns the
   * second into the first. Neither is an error, and neither gets a fabricated
   * name.
   *
   * `nullish`, not `nullable`, for the same deploy-skew reason as `icon` and
   * `shipGateMode` on `VibeAppSchema`: the console (Vercel) and the backend
   * (Porter) ship independently, so a frontend one release AHEAD parses a list
   * response that omits this key entirely. A required key turns that window
   * into a whole-response parse failure — an org with apps is told it has
   * none, over a column nobody would have missed. Absent and `null` mean the
   * same thing here, so accept both.
   */
  createdBy: VibeAppCreatorSchema.nullish(),
  /**
   * This app's deploy history — a bounded window of recent attempts plus
   * lifetime counts. See `VibeAppDeploymentHistorySchema` for what each number
   * is measured over and for the one claim it cannot make.
   *
   * A THIRD deployment signal beside `latestDeployment` and
   * `servingDeployment`, and it answers what neither can: those two are single
   * rows, so an app that fails every other push and one that has never failed
   * present identically on them.
   *
   * `.nullish()` for the deploy-skew reason `createdBy` states in full — and
   * absent here means "the server predates this field", **never "this app has
   * no deployments"**. That second state is `{ total: 0, recent: [] }`, which
   * is a real answer with its own rendering ("no deploys"). Collapsing the two
   * would make a card that hides its history panel unable to say which of the
   * two happened, and hiding the panel is the correct response to only one of
   * them.
   */
  deploymentHistory: VibeAppDeploymentHistorySchema.nullish()
});

export type VibeAppListItemDto = z.infer<typeof VibeAppListItemSchema>;

export const ListVibeAppsResponseSchema = z.object({
  apps: z.array(VibeAppListItemSchema)
});
